import { useState, useEffect, Fragment } from 'react'
import { useLocation } from 'react-router'
import filter from 'lodash/filter'
import isNil from 'lodash/isNil'
import uniqBy from 'lodash/uniqBy'
import { useTranslation } from 'react-i18next'

import { Link } from 'features/i18n/router'
import { User } from 'lib/_types'
import {
  useGetMembersQuery,
  useInviteToGroupMutation,
} from 'store/api/yay/group'
import { useCurrentUser, useListUserFollowingsQuery } from 'store/api/yay/user'
import { useInfinityScrollSimple } from 'hooks'
import { ROUTE_PATH } from 'lib/paths'
import { useNavigate } from 'features/i18n/router'

import Avatar from '../Avatar'
import Nickname from '../Nickname'
import Loading from '../Loading'
import LoadMore from '../LoadMore'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'

let searchTO = null

const GroupInvitationModal = ({ groupId }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [
    __isActiveConfirmBoxInvitationSuccessful,
    __isActiveConfirmBoxInvitationSuccessfulSet,
  ] = useState(false)

  const [
    __isActiveConfirmBoxInvitationFailed,
    __isActiveConfirmBoxInvitationFailedSet,
  ] = useState(false)

  const [__checkIds, __checkIdsSet] = useState([])
  const [__search, __searchSet] = useState<string>('')
  const [__fromFollowId, __fromFollowIdSet] = useState<string>('0')
  const [__userFollowings, __userFollowingsSet] = useState<Array<User>>([])
  const [__userFollowingsSearch, __userFollowingsSearchSet] = useState<
    Array<User>
  >([])

  const { data: currentUser } = useCurrentUser()

  const isSearching = __search?.length > 0

  const usersFollowingsRes = useListUserFollowingsQuery({
    userId: currentUser?.id,
    ...(!isSearching && { fromFollowId: __fromFollowId }),
    nickname: __search,
  })

  const groupMembersRes = useGetMembersQuery({
    groupId,
  })

  const [inviteToGroup, inviteToGroupRes] = useInviteToGroupMutation()

  const loadMore = () => {
    __fromFollowIdSet(usersFollowingsRes?.data?.last_follow_id?.toString?.())
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    __userFollowingsSearchSet([])

    if (isSearching) {
      __userFollowingsSearchSet(usersFollowingsRes?.data?.users ?? [])
    } else {
      __userFollowingsSet(
        uniqBy(
          [
            ...__userFollowings?.map?.((x) => {
              return (
                usersFollowingsRes?.data?.users?.find?.(
                  (y) => y?.id == x?.id
                ) ?? x
              )
            }),
            ...(usersFollowingsRes?.data?.users ?? []),
          ],
          (x) => x?.id
        )
      )
    }
  }, [usersFollowingsRes])

  const unjoinedUsers = (
    isSearching ? __userFollowingsSearch : __userFollowings
  )?.filter?.((x: any) => {
    return isNil(groupMembersRes?.data?.find?.((y) => y?.user?.id == x?.id))
  })

  return (
    <Fragment>
      <form
        className="Modal__wrapper"
        onSubmit={(e) => {
          e.preventDefault()

          inviteToGroup({
            groupId,
            userIds: __checkIds,
          }).then((res) => {
            if (res?.['error']?.data?.error_code == -343) {
              __isActiveConfirmBoxInvitationFailedSet(true)
              return
            }

            if (res?.['data']?.result == 'success') {
              __isActiveConfirmBoxInvitationSuccessfulSet(true)
            }
          })
        }}
      >
        <div className="Modal__header">
          <Link className="Modal__header__close" to={location.pathname} />

          <h2 className="Modal__header__h2">{t('common.invitation')}</h2>
        </div>

        <div className="Modal__search">
          <div className="Search">
            <input
              className="Search__input"
              placeholder={t('common.search_friend_from_following')}
              type="text"
              onChange={(e) => {
                window.clearTimeout(searchTO)
                searchTO = window.setTimeout(() => {
                  __searchSet(e?.target?.value)
                }, 400)
              }}
            />
          </div>
        </div>

        <div className="Modal__body">
          {(usersFollowingsRes?.data?.users?.length ?? 0) == 0 &&
            usersFollowingsRes?.isLoading && <Loading />}

          {usersFollowingsRes?.isSuccess && (unjoinedUsers?.length ?? 0) === 0 && (
            <div className="Placeholder Placeholder--recommend-users">
              <p className="Placeholder__p">
                {t(
                  'group_invitation_modal.following_controller_empty_view_title'
                )}
              </p>
            </div>
          )}

          {unjoinedUsers?.length > 0 && (
            <dl className="RecommendUsers">
              {unjoinedUsers?.map?.((x, i) => (
                <label
                  className="RecommendUsers__item RecommendUsers__item--group-invite"
                  key={i}
                >
                  <div className="RecommendUsers__item__user">
                    <Avatar user={x} showVipCircle showOnlineStatus />

                    <div className="RecommendUsers__item__info">
                      <h3 className="RecommendUsers__item__h3">
                        <Nickname user={x} />
                      </h3>
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    value={x?.id}
                    onChange={(e) => {
                      __checkIdsSet(
                        filter(
                          e?.target?.form?.elements,
                          ($input: HTMLInputElement) => $input?.checked
                        )?.map?.(($input: HTMLInputElement) => $input?.value)
                      )
                    }}
                  />

                  <span className="RecommendUsers__item__circle" />
                </label>
              ))}

              {!isSearching && (
                <LoadMore
                  isLoading={usersFollowingsRes?.isFetching}
                  hidden={isNil(usersFollowingsRes?.data?.last_follow_id)}
                  className="RecommendUsers__load-more"
                  onClick={() => {
                    loadMore()
                  }}
                  ref={loadMoreRef}
                />
              )}
            </dl>
          )}
        </div>

        <div className="Modal__footer Modal__footer--minus-margin">
          <button
            className="Button"
            disabled={__checkIds?.length == 0 || inviteToGroupRes?.isLoading}
          >
            {t('common.send')}
          </button>
        </div>
      </form>

      <ConfirmBoxBasic
        isActive={__isActiveConfirmBoxInvitationSuccessful}
        onClose={() => {
          navigate(location.pathname)
        }}
        onOk={() => {
          navigate(location.pathname)
        }}
        title={t('group_invitation_modal.invitation_sent_confirm_box.title')}
        content={t(
          'group_invitation_modal.invitation_sent_confirm_box.content'
        )}
      />

      <ConfirmBoxBasic
        isActive={__isActiveConfirmBoxInvitationFailed}
        onClose={() => {
          __isActiveConfirmBoxInvitationFailedSet(false)
        }}
        onOk={() => {
          navigate(ROUTE_PATH.SETTING_SUBSCRIPTION)
        }}
        title="Error (-343)"
        content={t(
          'group_invitation_modal.error_quota_exceeded_message_vip_suggest.content'
        )}
        confirmButtonText={t(
          'group_invitation_modal.alert_vip_promote_action.content'
        )}
      />
    </Fragment>
  )
}

export default GroupInvitationModal
